import { useCallback, useEffect, useRef, useState } from 'react'
import { normalizeSearchTerm } from '../search/normalize'

interface SearchQueryOptions {
  /** Применённое значение: в том числе из URL или сохранённого вида. */
  value?: string
  /** Вызывается после паузы; запись URL остаётся у владельца страницы. */
  onChange?: (query: string) => void
}

export function useSearchQuery({ value, onChange }: SearchQueryOptions = {}) {
  const [input, setInput] = useState(value ?? '')
  const [query, setQuery] = useState(() => normalizeSearchTerm(value))
  const inputRef = useRef<HTMLInputElement>(null)
  const callbackRef = useRef(onChange)
  callbackRef.current = onChange
  const committedRef = useRef(value ?? '')
  const externalRef = useRef(value)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  const commit = useCallback((next: string) => {
    setQuery(next)
    if (committedRef.current !== next) {
      committedRef.current = next
      callbackRef.current?.(next)
    }
  }, [])

  // Назад/вперёд в браузере и сохранённые виды отменяют незавершённый ввод.
  useEffect(() => {
    if (value === externalRef.current) return
    externalRef.current = value
    if (value === undefined || value === committedRef.current) return
    clearTimeout(timerRef.current)
    committedRef.current = value
    setInput(value)
    setQuery(normalizeSearchTerm(value))
  }, [value])

  useEffect(() => {
    timerRef.current = setTimeout(() => commit(normalizeSearchTerm(input)), 350)
    return () => clearTimeout(timerRef.current)
  }, [input, commit])

  const clear = useCallback(() => {
    clearTimeout(timerRef.current)
    setInput('')
    commit('')
    inputRef.current?.focus()
  }, [commit])

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.defaultPrevented || event.isComposing
        || event.ctrlKey || event.altKey || event.metaKey) return
      const target = event.target instanceof HTMLElement ? event.target : null
      if (target?.closest('input, textarea, select') || target?.isContentEditable
        || target?.closest('[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]')) return
      if (!inputRef.current) return
      event.preventDefault()
      inputRef.current.focus()
    }
    document.addEventListener('keydown', focusSearch)
    return () => document.removeEventListener('keydown', focusSearch)
  }, [])

  return { input, setInput, query, inputRef, clear }
}
